<template>
  <div>
    <!-- Desktop-only skeleton while Optimizely flag is pending. -->
    <div
      v-if="!flagDecided"
      aria-hidden="true"
      class="lg:grid lg:cols-2 lg:gap-1 lg:overflow-visible lg:pl-6 <lg:hidden"
    >
      <div
        v-for="i in 6"
        :key="i"
        class="skeleton"
        :class="{ 'col-span-2': [3, 4].includes(i) }"
        :style="{ 'aspect-ratio': src.width / src.height }"
      />
    </div>
    <div v-show="flagDecided">
      <vf-media-gallery v-if="!mediaGalleryHorizontalScroll?.enabled" v-bind="forwarded">
        <template #default="{ i, item }">
          <slot v-bind="{ i, item }" />
        </template>
      </vf-media-gallery>
      <vf-carousel-elevate
        v-else-if="items.length"
        ref="carouselRef"
        align="start"
        :class-container="{ ['lg:nowrap lg:gap-1']: alwaysUseGrid || items?.length > 1 }"
        data-test-id="vf-media-gallery"
        :loop="false"
        :slides-to-scroll="1"
      >
        <template #default>
          <div
            v-for="(item, i) in items"
            :key="'src' in item ? item.src : i"
            class="group relative <md:w-full"
            :class="[getItemClass ? getItemClass(i) : { [brandClasses.carouselItemFirst || '']: i === 0 }, brandClasses.carouselItem]"
          >
            <cms-section v-if="item.type === 'espot'" :section="item.section" />
            <template v-if="item.type === 'image'">
              <vf-zoomer
                v-if="item.srcZoom"
                :alt="isAltTextRepeated ? `${item.alt} - ${i + 1}` : item.alt"
                :height="presets.height"
                :lazy="i >= 4"
                :props-img="{
                  class: 'w-full lg:w-a lg:h-[var(--gallery-h)]',
                  style: { '--gallery-h': galleryHeight },
                }"
                :src="getImageSrc(item)"
                :src-zoom="item.srcZoom"
                :width="presets.width"
                @zoom="$zoomFactor => $emit('zoom', i, $zoomFactor)"
              />
              <base-picture
                v-else
                v-style:height="{ lg: galleryHeight }"
                :alt="isAltTextRepeated ? `${item.alt} - ${i + 1}` : item.alt"
                class="w-full lg:w-a"
                :height="presets.height"
                :lazy="i >= 4"
                :src="getImageSrc(item)"
                :width="presets.width"
              />

              <slot v-bind="{ i, item }" />
            </template>
            <vf-video
              v-if="item.type === 'video'"
              ref="videos"
              v-bind="item"
              class="full bg-black"
              :controlslist
              fit="cover"
              :style="{ height: $viewport.lg ? galleryHeight : undefined }"
              @play="handlePlay(item)"
            />

            <sketchfab v-if="item.type === 'sketchfab' && enableSketchfab" :product-id="item.productId" />
          </div>
        </template>

        <template #prev="{ active, action }">
          <base-button
            :aria-label="$t.previousSlide"
            class="absolute left-0 top-1/2 z-2 duration -mt-4 lg:hidden -scale-x-100"
            :class="[{ 'pointer-events-none op-0': !gallery.loop && !active, 'hidden': !showPrevNext || items.length < 2 }]"
            data-test-id="vf-carousel-arrow-left"
            @click.stop="action"
          >
            <vf-icon name="chevron" size="lg" />
          </base-button>
        </template>

        <template #next="{ active, action }">
          <base-button
            :aria-label="$t.nextSlide"
            class="absolute right-0 top-1/2 z-2 duration -mt-4 lg:hidden"
            :class="[{ 'pointer-events-none op-0': !gallery.loop && !active, 'hidden': !showPrevNext || items.length < 2 }]"
            data-test-id="vf-carousel-arrow-right"
            @click.stop="action"
          >
            <vf-icon name="chevron" size="lg" />
          </base-button>
        </template>

        <template v-if="showPagination" #pagination="{ activeItem }">
          <div v-if="pagination && items.length" class="absolute inset-x-0 bottom-0 z-10">
            <vf-media-gallery-pagination
              :active-page="activeItem"
              :pages="items.length"
              @navigate="carouselRef?.scrollTo"
            />
          </div>
        </template>
      </vf-carousel-elevate>
      <div v-else class="bg-grey-80 py-1/2 text-center lh-0">
        {{ $t.imageUnavailable }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Emits, Props } from '#brands/components/vf/media-gallery/MediaGallery.vue'
import type { BaseVideo as BaseVideoType } from '#components'
import type { Responsive } from '#types/common'
import type { ImageItem } from '#types/media'
import VfMediaGallery from '#brands/components/vf/media-gallery/MediaGallery.vue'

const props = defineProps<Props>()
const emits = defineEmits<Emits>()

defineSlots<{
  default: (props: { i: number, item: ImageItem }) => void
}>()

const mediaGalleryHorizontalScroll = useOptimizelyFlag('media_gallery_horizontal_scroll')
const header = useHeaderStore()

const galleryHeight = computed(() => {
  const headerHeightRem = pxToRem(header.height.full + 24)
  return `max(44rem, calc(100dvh - ${headerHeightRem}))`
})

const forwarded = useForwardPropsEmits(props, emits)

const {
  alwaysUseGrid,
  brandClasses,
  controlslist,
  showPagination,
  showPrevNext
} = useAppConfig().components.vf.mediaGallery
const { enableSketchfab, gallery } = useAppConfig().pages?.pdp || {}
const { $viewport } = useNuxtApp()
const { src } = useAppConfig().pages.pdp.gallery.presets
const { decisions } = useOptimizely()
const flagDecided = computed(() => decisions.value.has('media_gallery_horizontal_scroll'))

const imageSource = computed(() => getValueForBreakpoint($viewport.breakpoint, gallery.imageSource) || 'src')

const isAltTextRepeated = computed(() =>
  props.items.length > 1 && props.items.every((item) => item.alt === props.items[0].alt))
const presets = computed(
  () => alwaysUseGrid || props.items.length > 1 ? gallery.presets.src : gallery.presets[imageSource.value]
)

const getImageSrc = (item) => Object.fromEntries(
  Object.entries(gallery.imageSource).map(([breakpoint, key]) => [
    breakpoint,
    props.items.length === 1 ? item[key as keyof typeof item] || item.src : item.src
  ])
) as Responsive

const videos = ref<typeof BaseVideoType[]>([])
const carouselRef = ref<any>(null)

const handlePlay = ({ src }) => {
  const resolvedSrc = getValueForBreakpoint($viewport.breakpoint, src)
  const startingVideo = videos.value.find(
    ({ videoRef }) => getValueForBreakpoint($viewport.breakpoint, videoRef.src) === resolvedSrc
  )
  if (startingVideo && !startingVideo.videoRef.el.muted) {
    videos.value
      .filter((video) => video !== startingVideo && !video.videoRef.el.muted)
      .forEach((video) => video.pause())
  }
}
</script>
